/** PostgREST query fragments for the listing catalogues — ONE definition, two
 *  readers.
 *
 *  The SSR worker renders a page of listings from these; the hydrated SPA reads
 *  the SAME rows for the one surface `/catalog/search` cannot serve (the landing
 *  page's featured pool — see `LISTING_FEATURED_ORDER`). A second copy of either
 *  the select or the order would drift, and the drift is invisible in the worst
 *  possible way: the server renders a page and the client silently replaces it
 *  with a different one on hydration.
 *
 *  Everything else the SPA browses goes through `/catalog/search`, which owns
 *  its own semantics (migrations 029/030, proven by differential against
 *  `runDiscover`). These fragments exist for the reads that are NOT a search.
 */

export type ListingCatalogue = 'recipes' | 'packs';

/** The stable per-kind identity column — every order's final tiebreak. */
export const LISTING_KEY = {
  recipes: 'recipe_id',
  packs: 'slug',
} as const satisfies Record<ListingCatalogue, string>;

/** Public rows only. Both readers use the anon key, and the `status='published'`
 *  RLS policy already enforces this — the predicate is belt-and-braces, and it
 *  keeps the counts honest against a role that could see more. */
export const LISTING_FILTER = 'status=eq.published';

/** Every column a browse card, a filter, a sort or a sitemap entry reads.
 *
 *  ⚠ NO BODIES. `recipe` / `manifest` are 96.6% and 97.6% of their tables and no
 *  list surface has ever rendered any of them; the scalars that used to justify
 *  reading them (`provenance_opted_out`, `depends_on`, `recipe_bundle`,
 *  `item_count`, `visible_item_count`, `service_kind`) are GENERATED columns
 *  since migration 028. A surface that needs a body fetches that ONE row. */
export const LISTING_SELECT = {
  recipes:
    'recipe_id,publisher_id,name,description,version,platforms,tags,download_count,view_count,rating_avg,rating_count,created_at,updated_at'
    + ',provenance_opted_out,recipe_bundle,depends_on',
  packs:
    'slug,publisher_id,name,description,version,pack_kind,tags,download_count,view_count,created_at,updated_at'
    + ',service_kind,item_count,visible_item_count',
} as const satisfies Record<ListingCatalogue, string>;

/** The browse order — newest first, key ascending.
 *
 *  ⚠ Byte-identical to the `newest` sorter in `discover-specs.ts` (key AND
 *  tiebreak), which is what lets the SSR list and a `sort=newest` search agree
 *  on which rows a page contains. */
export const LISTING_BROWSE_ORDER = (kind: ListingCatalogue): string =>
  `created_at.desc,${LISTING_KEY[kind]}.asc`;

/** The landing page's FEATURED order.
 *
 *  ⚠ The middle term is load-bearing. With one nonzero `download_count` in the
 *  whole corpus the ranking is a sea of ties, so whatever breaks them decides
 *  the homepage: `download_count,<key>` alone silently features the alphabet.
 *
 *  Deliberately NOT `/catalog/search`. Its `downloads` and `popular` sorters
 *  tiebreak by the listing key, so the endpoint cannot express this order — and
 *  a featured pool is a curated top-N, not a search result. Both renderers of
 *  the landing page read this one string instead. */
export const LISTING_FEATURED_ORDER = (kind: ListingCatalogue): string =>
  `download_count.desc,created_at.desc,${LISTING_KEY[kind]}.asc`;
